import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  COMPANY_NAME,
  QUESTION,
  QUESTION_SOURCE_TYPE,
  QUESTION_TYPE,
  SOURCE,
} from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import { useCreateQuestionsMutation, useGetMaterialityStakeholdersQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { formatTextTitle } from 'utils/formatText';

import useCreateQuestionForm from './hooks/useCreateQuestionForm';

import type IQuestionBody from 'store/slices/materialAssessment/interfaces/IQuestionBody';
import type IFieldError from 'store/types/IFieldError';
import type { TQuestionCreateSchema } from 'utils/validators';
import type ICreateQuestion from './interfaces/ICreateQuestion';

import styles from './scss/CreateQuestion.module.scss';

function CreateQuestion({
  open, onClose, title, recordId, setOpenModal, vcmId, onQuestionGroupCreated,
}: ICreateQuestion) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createQuestions] = useCreateQuestionsMutation();
  const { setFieldsErrors } = useFieldsErrors();

  const {
    data: stakeholdersData,
    isLoading: isStakeholdersLoading,
  } = useGetMaterialityStakeholdersQuery({ maId: recordId || '', vcmId: vcmId || 0 });

  const isLoading = isStakeholdersLoading;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
  } = useCreateQuestionForm({ isPageLoading: isLoading });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onAddQuestion = (question: IQuestionBody) => {
    createQuestions({ maId: recordId || '', data: question, vcmId: vcmId || 0 }).then(() => {
      toast.success(i18n.t('notifications.createQuestionGroup'));
      setOpenModal('');
      onQuestionGroupCreated?.();
    }).catch((errors) => {
      setFieldsErrors<TQuestionCreateSchema>({ errors, setError, fieldsName });
    });
  };

  const onCreateInvestorSubmit = handleSubmit((data) => {
    const source = data[SOURCE].trim();

    onAddQuestion({
      text: data[QUESTION],
      type: data[QUESTION_TYPE]?.value || '',
      sourceType: data[QUESTION_SOURCE_TYPE]?.value || '',
      companyName: data[COMPANY_NAME] || '',
      source,
    });
    onCancel();
  });

  const optionCategory = [
    {
      value: 'environmental',
      label: 'Environmental',
    },
  ];

  const optionStakeholders = (stakeholdersData?.data?.items || []).map((item) => ({
    value: item.type,
    label: formatTextTitle(item.type),
  }));

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={QUESTION}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.question')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                required
                placeholder={t('materialityAssessment.placeholder.typeWebsite')}
              />
            )}
          />
          <Controller
            control={control}
            name={QUESTION_TYPE}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.questionType')}
                placeholder={t('materialityAssessment.placeholder.selectType')}
                options={optionCategory}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />

          <Controller
            control={control}
            name={QUESTION_SOURCE_TYPE}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.questionSourceType')}
                placeholder={t('materialityAssessment.placeholder.selectType')}
                options={optionStakeholders}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={COMPANY_NAME}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.companyName')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.contactName')}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={SOURCE}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value || ''}
                label={t('materialityAssessment.fields.source')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.create')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateQuestion;
